// Source-of-truth content log loaded from /api/data/content. Mirrors the
// ClientsContext pattern: async load on mount, optimistic updates,
// safety guard against accidentally wiping the list.

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../utils/apiFetch';

const ContentContext = createContext(null);

async function fetchAll() {
  const res = await apiFetch('/api/data/content');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch content log');
  return data.content || [];
}

async function postOne(entry) {
  const res = await apiFetch('/api/data/content', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create content entry');
  return data.content;
}

async function putOne(id, entry) {
  const res = await apiFetch(`/api/data/content/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update content entry');
  return data.content;
}

async function deleteOne(id) {
  const res = await apiFetch(`/api/data/content/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete content entry');
  return data;
}

export function ContentProvider({ children }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialized = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const live = await fetchAll();
      setEntries(live);
      setError(null);
      return live;
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const saveOne = useCallback(async (entry) => {
    if (entry.id) {
      const saved = await putOne(entry.id, entry);
      setEntries(prev => prev.map(e => e.id === saved.id ? saved : e));
      return saved;
    }
    const saved = await postOne(entry);
    setEntries(prev => [saved, ...prev]);
    return saved;
  }, []);

  const removeOne = useCallback(async (id) => {
    const optimistic = entries.filter(e => e.id !== id);
    setEntries(optimistic);
    try {
      await deleteOne(id);
    } catch (e) {
      console.error('[ContentContext] delete failed:', e);
      refresh();
      throw e;
    }
  }, [entries, refresh]);

  const value = { entries, loading, error, refresh, saveOne, removeOne };
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}
